/**
 * When CRX's own helpers (a price list waiting for its supplier, a file waiting
 * for its mission, a half-filled "new company") may take the person's next
 * message as their answer. Only a short reply that reads like an answer does:
 * a question, a remark or a new request goes to the AI instead (2026-11-02:
 * "CRX is simply an abbreviation for Crownox; both names refer to the same
 * company" was taken for a new company's name, and the long brief after it for
 * the company's address).
 */

const ASKS = /^(?:please|pls|can|could|would|will|should|do|does|did|what|why|how|when|where|who|which|write|prepare|draft|summari[sz]e|analy[sz]e|compare|find|search|explain|give|show|tell|list|help)\b/i;
// words of a remark about the conversation, not an answer to the helper
const REMARK = /\b(?:abbreviation|refers?|both|same|means|actually|because|i mean|isn't|aren't|doesn't|don't|wrong|not what)\b/i;

/** A short answer to a helper's question ("Acme Holdings", "the provider is ABC and based in Oman", "+968 2412 3456", "skip"). */
export const looksLikeAReply = (text: string): boolean => {
    const t = String(text ?? "").trim();
    if (!t || t.length > 120 || t.split(/\r?\n/).length > 2 || /[?;]/.test(t)) return false;
    return !ASKS.test(t) && !REMARK.test(t);
};

/** Clearly a new task, not a side remark: long, or several lines (a pasted brief, a full request). */
export const isNewTask = (text: string): boolean => {
    const t = String(text ?? "").trim();
    return t.length > 300 || t.split(/\r?\n/).filter((line) => line.trim()).length > 3;
};

const REQUEST_START = /^(?:please|pls|can you|could you|would you|i need|i want|i'd like|help me|prepare|write|draft|make|create|summari[sz]e|analy[sz]e|check|compare|find|review|translate|give me|put|turn|list|explain|calculate|work out)\b/i;

/**
 * The request at the top of a long paste, when the person wrote one ("Can you
 * prepare a benchmarking report … based on the information provided?"), so it
 * becomes the question and the whole paste the material. Null when the paste
 * is only material.
 */
export const requestOf = (text: string): string | null => {
    const first = String(text ?? "").trim().split(/\r?\n/).find((line) => line.trim()) ?? "";
    // the first sentence of the first line, up to its question mark or full stop
    const sentence = (/^[\s\S]*?[?.](?=\s|$)/.exec(first)?.[0] ?? first).trim();
    if (!sentence || sentence.length > 400) return null;
    return sentence.endsWith("?") || REQUEST_START.test(sentence) ? sentence : null;
};

/** What Ask offers when a file or a paste comes without words: one tap each, no AI until one is chosen. */
export const TASK_CHOICES = ["Summarise it", "Put its figures in a table", "Draft a report from it", "Check its legal terms", "Find related sources on the web"];
